import React, { useCallback, useEffect, useMemo, useState } from "react";
import BlockUi from "react-block-ui";
import DataTable from "react-data-table-component";
import ApiKeyReveal from "../components/ApiKeyReveal";
import CalculatorApiTabs from "../components/CalculatorApiTabs";
import { describeApiError } from "../utils/describeApiError";
import { createClient, getClient, listClients, rotateApiKey } from "../services";
import { buildClientColumns } from "./columns";
import JusfyClientsTable from "./JusfyClientsTable";

const SERVICE_UNAVAILABLE_MESSAGE =
  "Serviço de clientes indisponível. Tente novamente mais tarde.";

// A confirmação é a única barreira antes de um vínculo IRREVERSÍVEL: o
// calculator-api não valida o `backendClientId` contra a nossa base (aceita
// qualquer inteiro positivo, responde 201) e não há PATCH nesta entrega para
// desfazer. Por isso ela nomeia o cliente e o id — é onde o admin confere.
const CREATE_LINK_CONFIRMATION = (clientName, jusfyClientId) =>
  `Criar um cliente do Calculator API para ${clientName} ` +
  `(cliente ${jusfyClientId} da Jusfy)?\n\n` +
  "A API key é emitida na hora e já autentica. Repasse por canal seguro.";

const ROTATE_CONFIRMATION = clientName =>
  `Rotacionar a API key de ${clientName}?\n\n` +
  "A key atual PARA DE FUNCIONAR IMEDIATAMENTE — a integração do cliente cai " +
  "até ele trocar o segredo do lado dele. Não há período de graça.\n\n" +
  "Se ele apenas perdeu a key, cancele e use \"Ver API key\" para reexibir a " +
  "mesma.";

export function CalculatorApiClients() {
  const [clients, setClients] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [listError, setListError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [loading, setLoading] = useState(false);

  // A credencial vive SÓ aqui, em estado local e efêmero: sai ao desmontar, e
  // nunca entra em Redux — redux-persist a gravaria em localStorage.
  const [revealedClient, setRevealedClient] = useState(null);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      setClients(await listClients());
      setListError(null);
    } catch (error) {
      setListError(describeApiError(error, SERVICE_UNAVAILABLE_MESSAGE));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Qual cliente da Jusfy já tem cliente no Calculator API. `backendClientId`
  // null fica de fora: é cliente interno do calculator-api, sem contraparte na
  // Jusfy — a lista deixou de filtrar por vínculo e agora traz os dois tipos.
  //
  // Chaveado por string porque os dois lados são APIs diferentes: o vínculo vem
  // number do calculator-api e o id da listagem da Jusfy pode vir string. Com
  // Map por valor cru, 42 !== "42" e todo mundo apareceria como não vinculado.
  const linkByJusfyClientId = useMemo(
    () =>
      new Map(
        clients
          .filter(client => client.backendClientId !== null)
          .map(client => [String(client.backendClientId), client]),
      ),
    [clients],
  );

  // O ÚNICO caminho de provisionamento (RF-21): o nome e o vínculo saem da linha
  // clicada, então não há id digitado à mão para errar — é por isso que não
  // existe form de criação nesta tela. Mandar o vínculo já na criação é também o
  // que torna a operação reconciliável se a rede cair — sem ele, nome duplicado
  // é aceito e não dá para saber o que foi criado.
  //
  // Não existe form de EDIÇÃO — o PATCH está fora de escopo (feature-spec §6),
  // então nome e vínculo errados na criação só se corrigem pela v3, e inativar
  // cliente não é possível pela tela.
  //
  // `jusfyClientId` chega pronto da tabela: é o id do CLIENTE, não de usuário, e
  // já vem number (o contrato recusa string com 422).
  const createLinkFor = async ({ jusfyClientId, name }) => {
    if (!window.confirm(CREATE_LINK_CONFIRMATION(name, jusfyClientId))) return;

    setActionError(null);
    setLoading(true);
    try {
      setRevealedClient(await createClient({ name, jusfyClientId }));
      await loadClients();
    } catch (error) {
      // 502 não diz se o cliente foi criado. NÃO repetir automaticamente: a
      // rota não é idempotente. Para reconciliar, procure o cliente na lista
      // abaixo — o vínculo é único, é o que gera o 409.
      setActionError(describeApiError(error, SERVICE_UNAVAILABLE_MESSAGE));
    } finally {
      setLoading(false);
    }
  };

  const showApiKey = async client => {
    setActionError(null);
    setLoading(true);
    try {
      setRevealedClient(await getClient(client.id));
    } catch (error) {
      setActionError(describeApiError(error, SERVICE_UNAVAILABLE_MESSAGE));
    } finally {
      setLoading(false);
    }
  };

  const rotate = async client => {
    if (!window.confirm(ROTATE_CONFIRMATION(client.name))) return;

    setActionError(null);
    setLoading(true);
    try {
      setRevealedClient(await rotateApiKey(client.id));
      await loadClients();
    } catch (error) {
      setActionError(describeApiError(error, SERVICE_UNAVAILABLE_MESSAGE));
    } finally {
      setLoading(false);
    }
  };

  const columns = buildClientColumns({
    onShowApiKey: showApiKey,
    onRotate: rotate,
  });

  // A rota de clientes do calculator-api não pagina e devolve a lista inteira,
  // então busca e paginação desta tabela são do front mesmo.
  const filteredClients = useMemo(() => {
    const term = nameFilter.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter(client => client.name.toLowerCase().includes(term));
  }, [clients, nameFilter]);

  return (
    <>
      <CalculatorApiTabs />

      <BlockUi tag="div" blocking={loading}>
        {actionError && (
          <div className="alert alert-warning" role="alert">
            {actionError}
          </div>
        )}

        {revealedClient && (
          <ApiKeyReveal
            clientName={revealedClient.name}
            apiKey={revealedClient.apiKey}
          />
        )}

        {/* O único caminho de criação da tela: todo cliente nasce vinculado, a
            partir da linha clicada — não há form onde digitar nome ou id. */}
        <h4>Clientes da Jusfy</h4>
        <p className="text-muted">
          Ache o cliente e clique em <strong>Criar vínculo</strong>: o cliente é
          criado no Calculator API já vinculado, e a API key é emitida na hora —
          ela aparece aqui em cima, mascarada. O nome sai da linha e não pode ser
          corrigido depois.
        </p>

        <JusfyClientsTable
          linkByJusfyClientId={linkByJusfyClientId}
          onCreateLink={createLinkFor}
        />

        <h4 className="mt-10">Clientes do Calculator API</h4>
        <p className="text-muted">
          Inclui os clientes internos, que não têm contraparte na Jusfy. Não há
          exclusão nem inativação por esta tela: para fechar o acesso de uma key
          comprometida, rotacione — o histórico de consumo é preservado.
        </p>

        {listError ? (
          <div className="alert alert-warning" role="alert">
            {listError}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={filteredClients}
            progressPending={loading}
            pagination
            noDataComponent="Nenhum cliente cadastrado."
            subHeader
            subHeaderComponent={
              <input
                type="text"
                className="form-control"
                style={{ width: 240 }}
                placeholder="Buscar por nome"
                value={nameFilter}
                onChange={event => setNameFilter(event.target.value)}
              />
            }
          />
        )}
      </BlockUi>
    </>
  );
}
